import React, { useState, Fragment, useRef } from 'react';
import { Combobox, Transition, Portal } from '@headlessui/react';
import { useFloating, offset, flip, size } from '@floating-ui/react';

// Icons
const ChevronUpDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.25rem', height: '1.25rem' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '1.25rem', height: '1.25rem' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

export default function ModernCombobox({ options = [], value, onChange, label, placeholder, disabled = false, allowTyping = true }) {
  const [query, setQuery] = useState('');
  const buttonRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value) || null;

  const filteredOptions =
    query === ''
      ? options
      : options.filter((opt) =>
          opt.label
            .toLowerCase()
            .replace(/\s+/g, '')
            .includes(query.toLowerCase().replace(/\s+/g, ''))
        );

  // Use Floating UI for proper positioning when using Portal
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [
      offset(4),
      flip({ padding: 8 }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  return (
    <div className="input-group">
      {label && <label>{label}</label>}
      <Combobox value={selectedOption} onChange={(opt) => onChange(opt ? opt.value : '')} disabled={disabled} nullable>
        {({ open }) => (
          <div style={{ position: 'relative' }}>
            
            {/* Input Container */}
            <div
              ref={refs.setReference}
              className="modern-combobox-wrapper"
              onMouseDown={(event) => {
                if (!open && buttonRef.current) {
                  if (!allowTyping) {
                    event.preventDefault();
                  }
                  buttonRef.current.click();
                }
              }}
            >
              <Combobox.Input
                className="modern-combobox-input"
                displayValue={(opt) => (opt ? opt.label : '')}
                onChange={(event) => {
                  if (allowTyping) {
                    setQuery(event.target.value);
                  }
                }}
                onClick={() => {
                  if (!allowTyping) {
                    return;
                  }
                  if (!open && buttonRef.current) {
                    setTimeout(() => {
                      buttonRef.current?.click();
                    }, 0);
                  }
                }}
                onFocus={() => {
                  if (!open && buttonRef.current) {
                    buttonRef.current.click();
                  }
                }}
                onKeyDown={(event) => {
                  if (!allowTyping) {
                    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      buttonRef.current?.click();
                      return;
                    }
                    if (event.key.length === 1) {
                      event.preventDefault();
                    }
                  }
                }}
                placeholder={placeholder}
                readOnly={!allowTyping}
              />
              <Combobox.Button ref={buttonRef} className="modern-combobox-btn">
                <ChevronUpDownIcon aria-hidden="true" />
              </Combobox.Button>
            </div>

            <Portal>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setQuery('')}
              >
                <Combobox.Options 
                  ref={refs.setFloating}
                  style={floatingStyles}
                  className="modern-combobox-options modern-combobox-options-portal"
                >
                  {filteredOptions.length === 0 && query !== '' ? (
                    <div className="modern-combobox-empty">
                      Nothing found.
                    </div>
                  ) : (
                    filteredOptions.map((opt) => (
                      <Combobox.Option
                        key={opt.value}
                        className={({ active }) =>
                          `modern-combobox-option ${active ? 'active' : ''}`
                        }
                        value={opt}
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={`option-label ${selected ? 'selected' : ''}`}>
                              {opt.label}
                            </span>
                            {selected ? (
                              <span className={`option-check ${active ? 'active' : ''}`}>
                                <CheckIcon aria-hidden="true" />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Combobox.Option>
                    ))
                  )}
                </Combobox.Options>
              </Transition>
            </Portal>
          </div>
        )}
      </Combobox>
    </div>
  );
}
